import React, { useEffect, useState } from 'react';
import { api, formatPhoneInput } from '../api';
import { AppSettings } from '../types';

const DEFAULT_SETTINGS: AppSettings = {
  venueTitle: '공인대 주점',
  maxTableNumber: 65,
  defaultDurationMinutes: 120,
};

const UserForm = () => {
  const [settings, setSettings] = useState<AppSettings>(DEFAULT_SETTINGS);
  const [tableNumber, setTableNumber] = useState('');
  const [name, setName] = useState('');
  const [phone, setPhone] = useState('');
  const [partySize, setPartySize] = useState('');
  const [error, setError] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    document.title = DEFAULT_SETTINGS.venueTitle;
  }, []);

  useEffect(() => {
    let cancelled = false;

    async function loadSettings() {
      try {
        const response = await api.getPublicBootstrap();
        if (cancelled) return;
        setSettings(response.settings);
        document.title = response.settings.venueTitle;
      } catch (loadError) {
        if (cancelled) return;
        setError(loadError instanceof Error ? loadError.message : '설정을 불러오지 못했습니다.');
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    }

    loadSettings();
    return () => {
      cancelled = true;
    };
  }, []);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (isSubmitting) return;

    if (!tableNumber || !name || !phone || !partySize) {
      setError('모든 항목을 입력해주세요.');
      return;
    }

    const tableNum = Number(tableNumber);
    if (!Number.isInteger(tableNum) || tableNum < 1 || tableNum > settings.maxTableNumber) {
      setError(`테이블 번호는 1에서 ${settings.maxTableNumber} 사이여야 합니다.`);
      return;
    }

    const partySizeNumber = Number(partySize);
    if (!Number.isInteger(partySizeNumber) || partySizeNumber < 1 || partySizeNumber > 20) {
      setError('인원수는 1명 이상 20명 이하여야 합니다.');
      return;
    }

    if (phone.replace(/\D/g, '').length < 10) {
      setError('전화번호 형식이 올바르지 않습니다.');
      return;
    }

    try {
      setIsSubmitting(true);
      setError('');
      const response = await api.createTable({
        tableNumber,
        name,
        phone,
        partySize: partySizeNumber,
      });

      window.location.hash = `#timer/${response.table.id}`;
    } catch (submitError) {
      setError(submitError instanceof Error ? submitError.message : '등록 중 오류가 발생했습니다.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div>
      <header className="page-header">
        <span className="page-badge">www.공인대주점.com</span>
        <h1>{settings.venueTitle}</h1>
        <p>테이블 번호, 대표자, 연락처, 인원을 입력하면 바로 이용 시간이 시작됩니다.</p>
      </header>

      <section className="hero-panel">
        <div>
          <strong>기본 이용 시간</strong>
          <span>{settings.defaultDurationMinutes}분</span>
        </div>
        <div>
          <strong>운영 테이블</strong>
          <span>1번부터 {settings.maxTableNumber}번까지</span>
        </div>
        <div>
          <strong>이용 시간 확인</strong>
          <span>등록 후 남은 시간이 바로 표시됩니다</span>
        </div>
      </section>

      <form onSubmit={handleSubmit}>
        <div className="form-group">
          <label htmlFor="tableNumber">테이블 번호</label>
          <input
            id="tableNumber"
            type="number"
            value={tableNumber}
            onChange={(e) => setTableNumber(e.target.value)}
            placeholder={`1 - ${settings.maxTableNumber}`}
            disabled={isSubmitting || loading}
          />
        </div>
        <div className="form-group">
          <label htmlFor="name">대표자 이름</label>
          <input
            id="name"
            type="text"
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="예: 김철수"
            disabled={isSubmitting || loading}
          />
        </div>
        <div className="form-group">
          <label htmlFor="phone">전화번호</label>
          <input
            id="phone"
            type="tel"
            value={phone}
            onChange={(e) => setPhone(formatPhoneInput(e.target.value))}
            maxLength={13}
            placeholder="010-1234-5678"
            disabled={isSubmitting || loading}
          />
        </div>
        <div className="form-group">
          <label htmlFor="partySize">총 인원</label>
          <input
            id="partySize"
            type="number"
            value={partySize}
            onChange={(e) => setPartySize(e.target.value)}
            placeholder="예: 4"
            disabled={isSubmitting || loading}
          />
        </div>
        {error ? <p className="form-error">{error}</p> : null}
        <button type="submit" disabled={isSubmitting || loading}>
          {isSubmitting ? '등록 중...' : '등록하기'}
        </button>
      </form>
    </div>
  );
};

export default UserForm;
